import { gql } from '@apollo/client/core';
import { useMutation } from '@vue/apollo-composable';
import type { LoginResponse, MutationResponse } from '@/types/composition-functions';

const userLoginMutation = () => useMutation<{ userLogin: LoginResponse }>(
  gql`
      mutation userLogin($email: String!, $password: String!) {
        userLogin(email: $email, password: $password) {
          token
          message
          status
          user {
            __typename
             ... on User {
              id
              name
              email
            }
          }
        }
      }
    `,
);

const unifiedLoginMutation = () =>
  useMutation<{ login: LoginResponse }>(
    gql`
      mutation login($identifier: String!, $password: String!) {
        login(identifier: $identifier, password: $password) {
          token
          message
          status
          school_count
          schools {
            id
            name
            logo
            school_type
            student_portal_mode
          }
          loginSchools {
            school {
              id
              name
              logo
              school_type
              student_portal_mode
            }
            portals
          }
          user {
            __typename
            ... on User {
              id
              name
              email
            }
            ... on Student {
              id
              matric_no
              first_name
              surname
              email
              school {
                school_type
                student_portal_mode
              }
            }
          }
        }
      }
    `,
  );

const studentResetPassword = () => useMutation<{ studentResetPassword: MutationResponse }>(
  gql`
      mutation studentResetPassword($matricNumber: String!, $password: String!) {
        studentResetPassword(matricNumber: $matricNumber, password: $password) {
           success
            message
            code
        }
      }
    `,
);

const FORGOT_PASSWORD = gql`
  mutation ForgotPassword($input: ForgotPasswordInput!) {
    forgotPassword(input: $input) {
      success
      message
    }
  }
`;

const RESET_PASSWORD = gql`
  mutation ResetPassword($input: ResetPasswordInput!) {
    resetPassword(input: $input) {
      success
      message
    }
  }
`;

const STUDENT_SET_PASSWORD_WITH_TOKEN = gql`
  mutation StudentSetPasswordWithToken($email: String!, $token: String!, $password: String!) {
    studentSetPasswordWithToken(email: $email, token: $token, password: $password) {
      success
      message
    }
  }
`;

const ADMIN_RESET_STUDENT_PASSWORD = gql`
  mutation AdminResetStudentPassword($input: AdminResetStudentPasswordInput!) {
    adminResetStudentPassword(input: $input) {
      success
      message
    }
  }
`;

const UPDATE_SCHOOL = gql`
  mutation UpdateSchool($id: ID!, $updateSchoolInput: UpdateSchoolInput!) {
    updateSchool(id: $id, updateSchoolInput: $updateSchoolInput) {
      id
      name
      email
      mobile
      website
      address
      ticket_auto_response
      attendance_type
      logo
      affiliate_logo
      affiliate_name
    }
  }
`;

const UPLOAD_SCHOOL_LOGO = gql`
  mutation UploadSchoolLogo($logo: Upload!) {
    uploadSchoolLogo(logo: $logo) {
      id
      name
      logo
    }
  }
`;

const UPLOAD_SCHOOL_AFFILIATE_LOGO = gql`
  mutation UploadSchoolAffiliateLogo($logo: Upload!) {
    uploadSchoolAffiliateLogo(logo: $logo) {
      id
      affiliate_logo
      affiliate_name
    }
  }
`;

const REMOVE_SCHOOL_AFFILIATE_LOGO = gql`
  mutation RemoveSchoolAffiliateLogo {
    removeSchoolAffiliateLogo {
      id
      affiliate_logo
      affiliate_name
    }
  }
`;

const UPLOAD_STUDENT_PHOTO = gql`
  mutation UploadStudentPhoto($student_id: ID!, $photo: Upload!) {
    uploadStudentPhoto(student_id: $student_id, photo: $photo) {
      id
      photo
    }
  }
`;

const UPLOAD_MY_PHOTO = gql`
  mutation UploadMyPhoto($photo: Upload!) {
    uploadMyPhoto(photo: $photo) {
      id
      photo
    }
  }
`;

const UPDATE_MY_PROFILE = gql`
  mutation UpdateMyProfile($email: String, $phone: String) {
    updateMyProfile(email: $email, phone: $phone) {
      id
      email
      phone
    }
  }
`;

const UPDATE_SCHOOL_SETTINGS = gql`
  mutation UpdateSchoolSettings($settings: JSON!) {
    updateSchoolSettings(settings: $settings) {
      id
      settings
    }
  }
`;

export {
  studentResetPassword,
  userLoginMutation,
  unifiedLoginMutation,
  FORGOT_PASSWORD,
  RESET_PASSWORD,
  STUDENT_SET_PASSWORD_WITH_TOKEN,
  ADMIN_RESET_STUDENT_PASSWORD,
  UPDATE_SCHOOL,
  UPLOAD_SCHOOL_LOGO,
  UPLOAD_SCHOOL_AFFILIATE_LOGO,
  REMOVE_SCHOOL_AFFILIATE_LOGO,
  UPLOAD_STUDENT_PHOTO,
  UPLOAD_MY_PHOTO,
  UPDATE_MY_PROFILE,
  UPDATE_SCHOOL_SETTINGS,
};

export * from './payment';
export * from './sessions';
export * from './Timetable';
export * from './programmeResultTemplate';
export * from './students';

export const DELETE_STAFF = gql`
  mutation DeleteStaff($staffId: ID!) {
    deleteStaff(staffId: $staffId)
  }
`;

// Deactivate / reactivate a staff membership in the current school (#225).
export const UPDATE_STAFF_STATUS = gql`
  mutation UpdateStaffStatus($staffId: ID!, $status: StaffStatus!, $reason: String) {
    updateStaffStatus(staffId: $staffId, status: $status, reason: $reason) {
      id
      status
    }
  }
`;

// "Don't show again" on the guided-tour intro (#571); acts on the caller only.
export const DISMISS_TOUR_INTRO = gql`
  mutation DismissTourIntro {
    dismissTourIntro
  }
`;

// "Don't show this again" on a one-off notice; stored on the staff account.
export const DISMISS_NOTICE = gql`
  mutation DismissNotice($notice: DismissibleNotice!) {
    dismissNotice(notice: $notice)
  }
`;

export const ASSIGN_STAFF_DEPARTMENT = gql`
  mutation AssignStaffDepartment($staff_id: ID!, $department_id: ID) {
    assignStaffDepartment(staff_id: $staff_id, department_id: $department_id) {
      id
      name
      department_id
      department {
        id
        department_name
        faculty_id
        faculty {
          id
          faculty_name
        }
      }
    }
  }
`;

export const CREATE_SCHOOL = gql`
  mutation CreateSchool($createSchoolInput: CreateSchoolInput!, $userRegisterInput: UserRegisterInput!) {
    createSchool(createSchoolInput: $createSchoolInput, userRegisterInput: $userRegisterInput) {
      id
      name
      address
      mobile
      website
      email
      logo
    }
  }
`;

export const CREATE_BILLS_FOR_GROUP = gql`
  mutation CreateBillsForGroup($input: CreateBillsInput!) {
    createBills(input: $input) {
      id
      amount
      due_date
      status
      billable_type
      billable_id
      academic_calendar_id
      session_id
      school_id
      billDetails {
        id
        amount
        bill_item_template_id
        billItemTemplate {
          id
          name
        }
      }
    }
  }
`;

export const CHANGE_PASSWORD = gql`
  mutation ChangePassword($input: ChangePasswordInput!) {
    changePassword(input: $input) {
      success
      message
    }
  }
`;

export const UPDATE_PDF_RESULT_CONFIG = gql`
  mutation UpdatePdfResultConfig($input: PdfResultConfigInput!) {
    updatePdfResultConfig(input: $input) {
      preset
      logo_position
      logo_height
      show_school_name
      show_school_address
      show_report_title
      report_title
      subject_column_width
      font_size
      header_font_size
      row_padding
      show_borders
      striped_rows
      alternate_row_color
      show_student_info
      student_info_fields
      show_summary
      show_grading_key
      show_attendance
      show_remarks
      show_footer
      footer_text
      page_size
      page_orientation
      page_margin
      show_signature_lines
      signature_labels
      show_watermark
      watermark_text
      show_next_term_date
    }
  }
`;
